/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { SCHEMA_VALIDATION_ERROR_TYPE, ValidatorString } from "@adapptio/lib-blueprint";
import { BEM } from "@adapptio/lib-components";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { FieldButton, FieldInput, Form, InputAdornment } from "@adapptio/lib-editor-ui";
import { COLOR } from "@adapptio/lib-editor-ui/src/enums/COLOR";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDeepCompareMemo } from "use-deep-compare";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import {
	DIALOG_CONTENT_ALIGN,
	DIALOG_CONTENT_FLOW,
	DialogContent
} from "../../../ui/components/Dialogs/DialogContent";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { FieldGroup } from "../../../ui/components/Fields/FieldGroup";
import { FieldRow } from "../../../ui/components/Fields/FieldRow";
import { FieldText } from "../../../ui/components/Fields/FieldText";
import { IResourceListProps, IResourceNode, ResourceList } from "../../../ui/components/ResourceList";
import { useEditorMainContext } from "../../../ui/contexts/EditorMainContext";
import { ImportBlueprintDialog } from "./ImportBlueprintDialog";
import { useFieldState, useStateWithEvent } from "../../../ui/hooks";

const allowedResourceTypes: RESOURCE_TYPES[] = [ RESOURCE_TYPES.DIRECTORY ];

export const ImportBlueprintDialogComponent: TDialogComponent<ImportBlueprintDialog> = ({ dialog }) => {
	const baseClass = BEM("edt-module-import-blueprint-dialog");

	const { appInfo } = useEditorMainContext();

	const inputFile = useRef(null);
	const [ fileUpload, setFileUpload ] = useState(null);

	const isBeingSaved = useStateWithEvent(dialog.onSaveStateChange, () => dialog.isBeingSaved());

	const [ selectedParentNode, setSelectedParentNode ] = useState<IResourceNode>(
		dialog.getParrentResourceNode()
	);

	const selectResource = useCallback<IResourceListProps["onSelect"]>((selectedNode) => {
		setSelectedParentNode(selectedNode);
	}, []);

	const basePath = useDeepCompareMemo(() => {
		if (selectedParentNode && !selectedParentNode?.isRoot) {
			return normalizeResourcePath(selectedParentNode.basePath, selectedParentNode.name);
		}
		return "";
	}, [ selectedParentNode ]);

	const [ fieldState, setFieldState ] = useFieldState(
		{
			name: {
				defaultValue: "",
				validators: [
					ValidatorString({ required: true, min: 1 }),
					(value: string) => {
						if (dialog.checkIfBlueprintExists(basePath, value)) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									// @todo Translate
									message: "Blueprint with this name already exists.",
									metaData: {}
								}
							];
						} else {
							return [];
						}
					}
				]
			}
		},
		[ dialog ]
	);

	useEffect(() => {
		if (fileUpload) {
			const fileNameArr = fileUpload.name.split(".");
			fileNameArr.pop();
			const name = fileNameArr.join(".");

			setFieldState.name(name);
		}
	}, [ fileUpload ]);

	useEffect(() => {
		if (fieldState.name.value.length) {
			setFieldState.name(fieldState.name.value);
		}
	}, [ basePath ]);

	const handleClose = useCallback(() => {
		dialog.close({
			resourceId: null
		});
	}, [ dialog ]);

	const handleCreate = useCallback(() => {
		dialog.import({
			basePath: basePath,
			name: fieldState.name.value,
			file: fileUpload
		});
	}, [ dialog, fieldState, basePath, fileUpload ]);

	const handleFile = useCallback(() => {
		inputFile.current.click();
	}, [ inputFile ]);

	const uploadFile = useCallback(() => {
		setFileUpload(inputFile.current.files[0]);
	}, [ inputFile ]);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL} className={baseClass.toClassName()}>
			<Form onSubmit={handleCreate} noValidate>
				<DialogHeader title="Import blueprint" icon="mdi/application-import" onClose={handleClose} />
				<DialogContent flow={DIALOG_CONTENT_FLOW.COLUMN} align={DIALOG_CONTENT_ALIGN.STRETCH}>
					<FieldRow>
						<FieldGroup>
							<FieldInput
								value=""
								type="file"
								inputRef={inputFile}
								onChange={uploadFile}
								accept=".yml,.yaml"
								hidden
								startAdornment={
									<InputAdornment>
										<FieldButton color={COLOR.PRIMARY} onClick={handleFile}>
											Choose File
										</FieldButton>
									</InputAdornment>
								}
								endAdornment={
									<InputAdornment>
										<label>{fileUpload ? fileUpload.name : ""}</label>
									</InputAdornment>
								}
							/>
						</FieldGroup>
					</FieldRow>

					<FieldRow>
						<FieldGroup>
							<FieldText
								value={fieldState.name.value}
								validationErrors={fieldState.name.validationErrors}
								placeholder="Resource Name"
								grow={true}
								onChange={setFieldState.name}
							/>
						</FieldGroup>
					</FieldRow>

					<label className={baseClass.element("field-row-resource-list").toClassName()}>
						<ResourceList
							onSelect={selectResource}
							labelSearch="Select target directory"
							allowedResourceTypes={allowedResourceTypes}
							selectedId={selectedParentNode?.id ?? "__root__"}
						/>
					</label>
				</DialogContent>
				<DialogButtons>
					<ActionButton
						label="Cancel"
						icon="mdi/close"
						style={ACTION_BUTTON_STYLE.DEFAULT}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleClose}
						disabled={isBeingSaved}
					/>
					<ActionButton
						label="Create"
						icon="mdi/check"
						style={ACTION_BUTTON_STYLE.PRIMARY}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleCreate}
						disabled={
							fieldState.name.validationErrors.length > 0 ||
							fieldState.name.value === "" ||
							isBeingSaved ||
							!fileUpload
						}
						isLoading={isBeingSaved}
					/>
				</DialogButtons>
			</Form>
		</DialogPanel>
	);
};
